import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import Iconify from "../../iconify/Iconify";
import {
  useDeleteEventMediaMutation,
  useDownloadEventMediaMutation,
  useGetEventMediaQuery,
  useUpdateEventMediaVisibilityMutation,
  useUploadEventMediaMutation,
} from "../../../features/events/eventsApiSlice";
import { setDialog } from "../../../features/dialog/dialogSlice";

const ACCEPTED_FILE_TYPES =
  ".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,image/*,application/pdf,text/plain,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size <= 0) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const normalizeFileIdentity = (file) =>
  [file?.name || "", file?.size || 0, file?.lastModified || 0].join("|");

export default function StepAttachments({
  eventId,
  isEdit,
  pendingFiles,
  onPendingFilesChange,
  isVisibleToMembers,
  onVisibilityChange,
  disabled,
}) {
  const dispatch = useDispatch();

  const {
    data: mediaPayload,
    isFetching: isMediaFetching,
    refetch: refetchMedia,
  } = useGetEventMediaQuery(eventId, {
    skip: !eventId || !isEdit,
  });
  const [uploadEventMedia, { isLoading: isReplacingMedia }] =
    useUploadEventMediaMutation();
  const [updateEventMediaVisibility, { isLoading: isUpdatingVisibility }] =
    useUpdateEventMediaVisibilityMutation();
  const [downloadEventMedia, { isLoading: isDownloading }] =
    useDownloadEventMediaMutation();
  const [deleteEventMedia, { isLoading: isDeletingMedia }] =
    useDeleteEventMediaMutation();

  const existingMedia = Array.isArray(mediaPayload?.media) ? mediaPayload.media : [];

  const hasBusyMutation =
    isReplacingMedia || isUpdatingVisibility || isDeletingMedia || isDownloading;
  const actionDisabled = disabled || hasBusyMutation;

  const handleSelectPendingFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;

    const existingKeys = new Set((pendingFiles || []).map(normalizeFileIdentity));
    const nextFiles = [...(pendingFiles || [])];
    selected.forEach((file) => {
      const fileKey = normalizeFileIdentity(file);
      if (existingKeys.has(fileKey)) return;
      existingKeys.add(fileKey);
      nextFiles.push(file);
    });

    onPendingFilesChange(nextFiles);
    event.target.value = "";
  };

  const handleRemovePendingFile = (targetIndex) => {
    onPendingFilesChange(
      (pendingFiles || []).filter((_, index) => index !== targetIndex)
    );
  };

  const handleReplaceAttachment = async (media, file) => {
    if (!eventId || !media?._id || !file) return;
    try {
      await uploadEventMedia({
        id: eventId,
        files: [file],
        replaceMediaId: media._id,
        isVisibleToMembers: Boolean(media.isVisibleToMembers),
      }).unwrap();
      await refetchMedia();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Anhang ersetzt",
          data: { successText: `${media.fileName || "Datei"} wurde ersetzt.` },
        })
      );
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Ersetzen fehlgeschlagen",
          data: {
            errorText: error?.data?.message || "Bitte erneut versuchen.",
          },
        })
      );
    }
  };

  const handleToggleVisibility = async (media) => {
    if (!eventId || !media?._id) return;
    try {
      await updateEventMediaVisibility({
        id: eventId,
        mediaId: media._id,
        isVisibleToMembers: !media.isVisibleToMembers,
      }).unwrap();
      await refetchMedia();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Sichtbarkeit konnte nicht geändert werden",
          data: {
            errorText: error?.data?.message || "Bitte erneut versuchen.",
          },
        })
      );
    }
  };

  const handleDeleteAttachment = async (media) => {
    if (!eventId || !media?._id) return;
    const confirmed = window.confirm(
      `Anhang "${media.fileName || "Datei"}" wirklich löschen?`
    );
    if (!confirmed) return;
    try {
      await deleteEventMedia({ id: eventId, mediaId: media._id }).unwrap();
      await refetchMedia();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Löschen fehlgeschlagen",
          data: {
            errorText: error?.data?.message || "Bitte erneut versuchen.",
          },
        })
      );
    }
  };

  const handleDownloadAttachment = async (media) => {
    if (!eventId || !media?._id) return;
    try {
      const blob = await downloadEventMedia({
        id: eventId,
        mediaId: media._id,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = media.fileName || "event-attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Download fehlgeschlagen",
          data: {
            errorText: error?.data?.message || "Datei konnte nicht geladen werden.",
          },
        })
      );
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Anhänge</Typography>
        <Typography variant="body2" color="text.secondary">
          Laden Sie Bilder oder Dokumente hoch. Bei öffentlichen Events können
          freigegebene Anhänge auch von öffentlichen Nutzern heruntergeladen werden.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              component="label"
              variant="outlined"
              startIcon={<Iconify icon="mdi:file-plus-outline" width={18} />}
              disabled={disabled}
            >
              Dateien auswählen
              <input
                hidden
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleSelectPendingFiles}
              />
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(isVisibleToMembers)}
                  onChange={(event) => onVisibilityChange(event.target.checked)}
                  disabled={disabled}
                />
              }
              label="Für Mitglieder freigeben (bei öffentlichen Events auch für öffentliche Nutzer)"
            />
          </Stack>

          {(pendingFiles || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Noch keine neuen Anhänge ausgewählt.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {(pendingFiles || []).map((file, index) => (
                <Paper key={normalizeFileIdentity(file)} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleRemovePendingFile(index)}
                      disabled={disabled}
                      startIcon={<Iconify icon="mdi:trash-can-outline" width={15} />}
                    >
                      Entfernen
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          {(pendingFiles || []).length > 0 ? (
            <Alert severity="info">
              Die ausgewählten Dateien werden beim Speichern der Veranstaltung hochgeladen.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {isEdit ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2">Bereits gespeicherte Anhänge</Typography>
            {isMediaFetching ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Anhänge werden geladen…
                </Typography>
              </Stack>
            ) : existingMedia.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Für dieses Event sind noch keine Anhänge gespeichert.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {existingMedia.map((media) => (
                  <Paper key={media._id} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {media.fileName || "Datei"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {media.sizeBytes ? formatFileSize(media.sizeBytes) : "Größe unbekannt"}
                        {media.mimeType ? ` · ${media.mimeType}` : ""}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleDownloadAttachment(media)}
                          disabled={actionDisabled}
                          startIcon={<Iconify icon="mdi:download" width={15} />}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleToggleVisibility(media)}
                          disabled={actionDisabled}
                          startIcon={
                            <Iconify
                              icon={
                                media.isVisibleToMembers
                                  ? "mdi:eye-off-outline"
                                  : "mdi:eye-outline"
                              }
                              width={15}
                            />
                          }
                        >
                          {media.isVisibleToMembers ? "Verbergen" : "Freigeben"}
                        </Button>
                        <Button
                          component="label"
                          size="small"
                          variant="outlined"
                          disabled={actionDisabled}
                          startIcon={<Iconify icon="mdi:file-replace-outline" width={15} />}
                        >
                          Ersetzen
                          <input
                            hidden
                            type="file"
                            accept={ACCEPTED_FILE_TYPES}
                            onChange={(event) => {
                              const nextFile = event.target.files?.[0];
                              if (nextFile) {
                                handleReplaceAttachment(media, nextFile);
                              }
                              event.target.value = "";
                            }}
                          />
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleDeleteAttachment(media)}
                          disabled={actionDisabled}
                          startIcon={<Iconify icon="mdi:trash-can-outline" width={15} />}
                        >
                          Löschen
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

StepAttachments.propTypes = {
  eventId: PropTypes.string,
  isEdit: PropTypes.bool,
  pendingFiles: PropTypes.arrayOf(PropTypes.object),
  onPendingFilesChange: PropTypes.func.isRequired,
  isVisibleToMembers: PropTypes.bool,
  onVisibilityChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

StepAttachments.defaultProps = {
  eventId: "",
  isEdit: false,
  pendingFiles: [],
  isVisibleToMembers: false,
  disabled: false,
};
